import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Divider,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Check, Clear, ArrowBack, LinkedIn, ThumbUp, ThumbDown } from '@mui/icons-material';
import { CampagneState, CampagneContent, Comment } from '../types/campagne';
import { Contact, CRMFilters, Statut, Effectif, ZoneGeographique } from '../types/crm';
import { crmData } from '../data/crmData';
import { CommentSection } from '../components/CommentSection';
import { SupabaseCommentSection } from '../components/SupabaseCommentSection';
import CampaignQuestionnaire from '../components/campaign/CampaignQuestionnaire';
import { v4 as uuidv4 } from 'uuid';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/supabase/AuthContext';
import { getCampaignById, updateCampaignStatus } from '../lib/supabase/campaignsService';
import { supabase } from '../lib/supabase/supabaseClient';
import { 
  getLeads, 
  updateLeadProspectionStage, 
  updateRdvStatus,
  updateMultipleLeadsQualificationStatus
} from '../lib/supabase/leadsService';
import { Database, QuestionnaireData } from '../types/supabase';

// Type pour le campaign data de Supabase
type Campaign = Database['public']['Tables']['campaigns']['Row'];
type Lead = Database['public']['Tables']['leads']['Row'];

// Fonction utilitaire pour convertir Json vers QuestionnaireData
const safeJsonToQuestionnaireData = (jsonData: any): QuestionnaireData => {
  if (jsonData && typeof jsonData === 'object' && !Array.isArray(jsonData)) {
    return jsonData as QuestionnaireData;
  }
  return {};
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`copywriting-tabpanel-${index}`}
      aria-labelledby={`copywriting-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const STORAGE_KEY = 'campagnes_state';
const CRM_STORAGE_KEY = 'crm_data';

const statuts: Statut[] = ['Pas encore contacté', 'Contacté'];
const effectifs: Effectif[] = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];
const zonesGeographiques: ZoneGeographique[] = ['France', 'Europe', 'Amérique du Nord', 'Amérique du Sud', 'Asie', 'Afrique', 'Océanie'];

export const CampagneDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [state, setState] = useState<CampagneState>(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    return savedData ? JSON.parse(savedData) : { campagnes: [], selectedCampagne: null };
  });
  const [isAdmin] = useState(true);
  const [contacts, setContacts] = useState<Contact[]>(() => {
    const savedData = localStorage.getItem(CRM_STORAGE_KEY);
    return savedData ? JSON.parse(savedData) : crmData;
  });
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [filters, setFilters] = useState<CRMFilters>({
    statut: 'tous',
    jobTitle: '',
    secteur: '',
    effectif: 'tous',
    zoneGeographique: 'tous',
    qualification: 'tous',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignData, setCampaignData] = useState<Campaign | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState<string | null>(null);

  // Rechercher la campagne dans les données locales
  const campagne = useMemo(() => {
    if (!id) return undefined;
    return state.campagnes.find(c => c.id === id);
  }, [state.campagnes, id]);

  // Détermine si nous utilisons les données Supabase ou les données locales
  const useSupabaseData = !campagne && !!campaignData;

  useEffect(() => {
    if (!id || !user) {
      navigate('/campagnes');
      return;
    }

    const fetchCampaignData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error: fetchError } = await getCampaignById(id, user.id);
        
        if (fetchError) {
          throw fetchError;
        }
        
        setCampaignData(data);
      } catch (err: any) {
        console.error('Erreur lors du chargement de la campagne:', err);
        setError(`Une erreur est survenue: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignData();
  }, [id, user, navigate]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    localStorage.setItem(CRM_STORAGE_KEY, JSON.stringify(contacts));
  }, [contacts]);

  // Charger les leads de la campagne lorsque l'ID change ou lors du changement d'onglet
  useEffect(() => {
    if (!id) return;
    
    const fetchLeads = async () => {
      if (!user) return;
      
      try {
        setLeadsLoading(true);
        setLeadsError(null);
        
        const { data, error: leadsError } = await getLeads(id, user.id);
        
        if (leadsError) {
          throw leadsError;
        }
        
        setLeads(data || []);
      } catch (err: any) {
        console.error('Erreur lors du chargement des leads:', err);
        setLeadsError(`Une erreur est survenue lors du chargement des leads: ${err.message}`);
      } finally {
        setLeadsLoading(false);
      }
    };
    
    // Charger les leads uniquement lorsqu'on accède à l'onglet Base de données
    if (tabValue === 2) {
      fetchLeads();
    }
  }, [id, tabValue, user]);

  // Conversion des leads Supabase au format Contact pour l'affichage
  const leadsAsContacts = useMemo(() => {
    return leads.map(lead => ({
      id: lead.id,
      statut: lead.prospection_etape as Statut,
      prenom: lead.prenom,
      nom: lead.nom,
      email: lead.email || '',
      phone: lead.telephone || '',
      linkedin: lead.linkedin || '',
      entreprise: lead.entreprise,
      jobTitle: lead.job_title || '',
      secteur: lead.secteur || '',
      effectif: lead.effectif as Effectif || '1-10',
      zoneGeographique: lead.zone as ZoneGeographique || 'France',
      estQualifie: lead.lead_qualifie,
      dateCreation: lead.created_at,
      dateMiseAJour: lead.updated_at,
    }));
  }, [leads]);

  // Utiliser soit les données du localStorage, soit les données Supabase
  const displayContacts = useSupabaseData ? leadsAsContacts : contacts;

  // Filtrage des contacts/leads
  const filteredContacts = useMemo(() => {
    // Si nous utilisons Supabase, nous filtrons uniquement les leads de la campagne actuelle
    const contactsToFilter = useSupabaseData ? leadsAsContacts : 
                               (campagne ? contacts : []);
    
    return contactsToFilter.filter(contact => {
      if (filters.statut !== 'tous' && contact.statut !== filters.statut) return false;
      if (filters.jobTitle && contact.jobTitle !== filters.jobTitle) return false;
      if (filters.secteur && contact.secteur !== filters.secteur) return false;
      if (filters.effectif !== 'tous' && contact.effectif !== filters.effectif) return false;
      if (filters.zoneGeographique !== 'tous' && contact.zoneGeographique !== filters.zoneGeographique) return false;
      
      switch (filters.qualification) {
        case 'qualifie':
          if (contact.estQualifie !== true) return false;
          break;
        case 'disqualifie':
          if (contact.estQualifie !== false) return false;
          break;
        case 'en_attente':
          if (contact.estQualifie !== null) return false;
          break;
      }
      
      return true;
    });
  }, [useSupabaseData, leadsAsContacts, contacts, campagne, filters]);

  // Mise à jour de la qualification pour les leads Supabase
  const handleQualification = async (isQualified: boolean) => {
    if (useSupabaseData) {
      try {
        setLeadsLoading(true);
        
        // Utiliser la nouvelle fonction pour mettre à jour plusieurs leads à la fois
        const { error: updateError } = await updateMultipleLeadsQualificationStatus(
          selectedContacts,
          isQualified
        );
        
        if (updateError) {
          throw updateError;
        }
        
        // Mettre à jour l'état local des leads
        setLeads(prevLeads => 
          prevLeads.map(lead => 
            selectedContacts.includes(lead.id) 
              ? { ...lead, lead_qualifie: isQualified, updated_at: new Date().toISOString() }
              : lead
          )
        );
        
        // Réinitialiser la sélection
        setSelectedContacts([]);
      } catch (err: any) {
        console.error('Erreur lors de la qualification des leads:', err);
        setLeadsError(`Une erreur est survenue: ${err.message}`);
      } finally {
        setLeadsLoading(false);
      }
    } else {
      // Logique existante pour les données locales
      setContacts(prev => prev.map(contact => {
        if (selectedContacts.includes(contact.id)) {
          return {
            ...contact,
            estQualifie: isQualified,
            dateMiseAJour: new Date().toISOString(),
          };
        }
        return contact;
      }));
      setSelectedContacts([]);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleApproval = (field: 'isMailApproved' | 'isLinkedInApproved') => () => {
    setState(prev => ({
      ...prev,
      campagnes: prev.campagnes.map(c =>
        c.id === id ? { ...c, [field]: !c[field] } : c
      )
    }));
  };

  const handleAddComment = (type: 'mail' | 'linkedin') => (text: string) => {
    const newComment: Comment = {
      id: uuidv4(),
      text,
      author: isAdmin ? "Admin" : "Utilisateur",
      timestamp: new Date().toISOString(),
      isResolved: false,
      responses: [],
    };

    setState(prev => ({
      ...prev,
      campagnes: prev.campagnes.map(c =>
        c.id === id
          ? {
              ...c,
              mailComments: type === 'mail' ? [...c.mailComments, newComment] : c.mailComments,
              linkedInComments: type === 'linkedin' ? [...c.linkedInComments, newComment] : c.linkedInComments,
            }
          : c
      )
    }));
  };

  const handleAddResponse = (type: 'mail' | 'linkedin') => (commentId: string, text: string) => {
    const newResponse = {
      id: uuidv4(),
      text,
      author: isAdmin ? "Admin" : "Utilisateur",
      timestamp: new Date().toISOString(),
    };

    setState(prev => ({
      ...prev,
      campagnes: prev.campagnes.map(c =>
        c.id === id
          ? {
              ...c,
              mailComments: type === 'mail'
                ? c.mailComments.map(comment =>
                    comment.id === commentId
                      ? { ...comment, responses: [...comment.responses, newResponse] }
                      : comment
                  )
                : c.mailComments,
              linkedInComments: type === 'linkedin'
                ? c.linkedInComments.map(comment =>
                    comment.id === commentId
                      ? { ...comment, responses: [...comment.responses, newResponse] }
                      : comment
                  )
                : c.linkedInComments,
            }
          : c
      )
    }));
  };

  const handleResolveComment = (type: 'mail' | 'linkedin') => (commentId: string) => {
    setState(prev => ({
      ...prev,
      campagnes: prev.campagnes.map(c =>
        c.id === id
          ? {
              ...c,
              mailComments: type === 'mail'
                ? c.mailComments.map(comment =>
                    comment.id === commentId
                      ? { ...comment, isResolved: !comment.isResolved }
                      : comment
                  )
                : c.mailComments,
              linkedInComments: type === 'linkedin'
                ? c.linkedInComments.map(comment =>
                    comment.id === commentId
                      ? { ...comment, isResolved: !comment.isResolved }
                      : comment
                  )
                : c.linkedInComments,
            }
          : c
      )
    }));
  };

  const handleDeleteComment = (type: 'mail' | 'linkedin') => (commentId: string) => {
    setState(prev => ({
      ...prev,
      campagnes: prev.campagnes.map(c =>
        c.id === id
          ? {
              ...c,
              mailComments: type === 'mail'
                ? c.mailComments.filter(comment => comment.id !== commentId)
                : c.mailComments,
              linkedInComments: type === 'linkedin'
                ? c.linkedInComments.filter(comment => comment.id !== commentId)
                : c.linkedInComments,
            }
          : c
      )
    }));
  };

  // Gestion des filtres CRM
  const handleFilterChange = (key: keyof CRMFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Gestion des sélections CRM
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedContacts(filteredContacts.map(contact => contact.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (contactId: string) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  // Extraction des valeurs uniques pour les sélecteurs (adaptées pour les données Supabase)
  const uniqueJobTitles = useMemo(() => {
    return Array.from(new Set(displayContacts.map(contact => contact.jobTitle)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'fr'));
  }, [displayContacts]);

  const uniqueSecteurs = useMemo(() => {
    return Array.from(new Set(displayContacts.map(contact => contact.secteur)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'fr'));
  }, [displayContacts]);

  const uniqueEffectifs = useMemo(() => {
    return Array.from(new Set(displayContacts.map(contact => contact.effectif)))
      .filter(Boolean)
      .sort((a, b) => {
        // Extraction des nombres pour comparaison
        const [minA] = a.split('-').map(Number);
        const [minB] = b.split('-').map(Number);
        return minA - minB;
      });
  }, [displayContacts]);

  const uniqueQualifications = useMemo(() => {
    const qualifications = new Set<string>();
    displayContacts.forEach(contact => {
      if (contact.estQualifie === true) qualifications.add('qualifie');
      else if (contact.estQualifie === false) qualifications.add('disqualifie');
      else if (contact.estQualifie === null) qualifications.add('en_attente');
    });
    return Array.from(qualifications)
      .sort((a, b) => {
        // Ordre personnalisé : Qualifiés, Disqualifiés, En attente
        const order = { 'qualifie': 1, 'disqualifie': 2, 'en_attente': 3 };
        return order[a as keyof typeof order] - order[b as keyof typeof order];
      });
  }, [displayContacts]);

  const handleQuestionnaireSaved = () => {
    // Recharger les données de la campagne pour obtenir les données mises à jour du questionnaire
    if (user && id) {
      getCampaignById(id, user.id)
        .then(({ data, error }) => {
          if (error) {
            console.error('Erreur lors du rechargement de la campagne:', error);
            return;
          }
          setCampaignData(data);
        });
    }
  };

  // Fonction pour valider un statut de campagne
  const handleValidateStatus = async (statusType: 'database_statut' | 'linkedin_statut' | 'mail_statut') => {
    if (!user || !id || !campaignData) return;

    try {
      setLoading(true);
      setError(null);

      const updates = { [statusType]: 'Validé' as const };
      const { data, error: updateError } = await updateCampaignStatus(id, updates, user.id);

      if (updateError) {
        throw updateError;
      }

      // Mettre à jour les données locales
      setCampaignData(data);

    } catch (err: any) {
      console.error('Erreur lors de la validation:', err);
      setError(`Erreur lors de la validation: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !campaignData && !campagne) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !campagne) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/campagnes')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
        <Typography variant="h4" component="h1">
          {campaignData?.nom || (campagne ? campagne.nom : 'Détails de la campagne')}
            </Typography>
        </Box>

      <Paper sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="campagne tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Mail" />
              <Tab label="LinkedIn" />
            <Tab label="Base de données" />
            <Tab label="Questionnaire" />
            </Tabs>
          </Box>

        {/* Tab for Mail */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Typography variant="h6">Email</Typography>
                {campagne && (
                  <IconButton
                    color={campagne.isMailApproved ? "success" : "default"}
                    onClick={handleApproval('isMailApproved')}
                  >
                    {campagne.isMailApproved ? <Check /> : <Clear />}
                  </IconButton>
                )}
                {useSupabaseData && campaignData?.mail_statut && (
                  <>
                    <Chip 
                      label={campaignData.mail_statut} 
                      color={campaignData.mail_statut === 'Validé' ? 'success' : 'default'} 
                      size="small" 
                    />
                    {campaignData.mail_statut === 'En attente' && (
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={() => handleValidateStatus('mail_statut')}
                        disabled={loading}
                      >
                        Valider Copy Email
                      </Button>
                    )}
                  </>
                )}
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                value={campaignData?.mail_copy || (campagne ? campagne.copywritingMail : '')}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
              {campagne && (
                <CommentSection
                  comments={campagne.mailComments}
                  onAddComment={handleAddComment('mail')}
                  onAddResponse={handleAddResponse('mail')}
                  onResolveComment={handleResolveComment('mail')}
                  onDeleteComment={handleDeleteComment('mail')}
                  isAdmin={isAdmin}
                />
              )}
              {useSupabaseData && campaignData && (
                <SupabaseCommentSection
                  campaignId={campaignData.id}
                  commentType="mail"
                  isAdmin={isAdmin}
                />
              )}
              </Grid>
            </Grid>
          </TabPanel>

        {/* Tab for LinkedIn */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Typography variant="h6">LinkedIn</Typography>
                {campagne && (
                  <IconButton
                    color={campagne.isLinkedInApproved ? "success" : "default"}
                    onClick={handleApproval('isLinkedInApproved')}
                  >
                    {campagne.isLinkedInApproved ? <Check /> : <Clear />}
                  </IconButton>
                )}
                {useSupabaseData && campaignData?.linkedin_statut && (
                  <>
                    <Chip 
                      label={campaignData.linkedin_statut} 
                      color={campaignData.linkedin_statut === 'Validé' ? 'success' : 'default'} 
                      size="small" 
                    />
                    {campaignData.linkedin_statut === 'En attente' && (
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={() => handleValidateStatus('linkedin_statut')}
                        disabled={loading}
                      >
                        Valider Copy LinkedIn
                      </Button>
                    )}
                  </>
                )}
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                value={campaignData?.linkedin_copy || (campagne ? campagne.copywritingLinkedIn : '')}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
              {campagne && (
                <CommentSection
                  comments={campagne.linkedInComments}
                  onAddComment={handleAddComment('linkedin')}
                  onAddResponse={handleAddResponse('linkedin')}
                  onResolveComment={handleResolveComment('linkedin')}
                  onDeleteComment={handleDeleteComment('linkedin')}
                  isAdmin={isAdmin}
                />
              )}
              {useSupabaseData && campaignData && (
                <SupabaseCommentSection
                  campaignId={campaignData.id}
                  commentType="linkedin"
                  isAdmin={isAdmin}
                />
              )}
              </Grid>
            </Grid>
          </TabPanel>

        {/* Tab for Database */}
        <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h5">
              Base de données - {campaignData?.nom || (campagne ? campagne.nom : '')}
            </Typography>
            {useSupabaseData && campaignData?.database_statut && (
              <>
                <Chip 
                  label={campaignData.database_statut} 
                  color={campaignData.database_statut === 'Validé' ? 'success' : 'default'} 
                  size="small" 
                />
                {campaignData.database_statut === 'En attente' && (
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    onClick={() => handleValidateStatus('database_statut')}
                    disabled={loading}
                  >
                    Valider Database
                  </Button>
                )}
              </>
            )}
          </Box>

            {leadsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {leadsError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {leadsError}
              </Alert>
            )}

          {/* Actions en masse */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<ThumbUp />}
              onClick={() => handleQualification(true)}
                disabled={selectedContacts.length === 0 || leadsLoading}
            >
              Qualifier la sélection
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<ThumbDown />}
              onClick={() => handleQualification(false)}
                disabled={selectedContacts.length === 0 || leadsLoading}
            >
              Disqualifier la sélection
            </Button>
          </Box>

          {/* Filtres */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Job Title</InputLabel>
              <Select
                value={filters.jobTitle}
                label="Job Title"
                onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
              >
                <MenuItem value="">Tous les job titles</MenuItem>
                {uniqueJobTitles.map(jobTitle => (
                  <MenuItem key={jobTitle} value={jobTitle}>{jobTitle}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Secteur</InputLabel>
              <Select
                value={filters.secteur}
                label="Secteur"
                onChange={(e) => handleFilterChange('secteur', e.target.value)}
              >
                <MenuItem value="">Tous les secteurs</MenuItem>
                {uniqueSecteurs.map(secteur => (
                  <MenuItem key={secteur} value={secteur}>{secteur}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Effectif</InputLabel>
              <Select
                value={filters.effectif}
                label="Effectif"
                onChange={(e) => handleFilterChange('effectif', e.target.value)}
              >
                <MenuItem value="tous">Tous les effectifs</MenuItem>
                {uniqueEffectifs.map(effectif => (
                  <MenuItem key={effectif} value={effectif}>{effectif}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Zone géographique</InputLabel>
              <Select
                value={filters.zoneGeographique}
                label="Zone géographique"
                onChange={(e) => handleFilterChange('zoneGeographique', e.target.value)}
              >
                <MenuItem value="tous">Toutes les zones</MenuItem>
                {zonesGeographiques.map(zone => (
                  <MenuItem key={zone} value={zone}>{zone}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Qualification</InputLabel>
              <Select
                value={filters.qualification}
                label="Qualification"
                onChange={(e) => handleFilterChange('qualification', e.target.value)}
              >
                <MenuItem value="tous">Tous</MenuItem>
                {uniqueQualifications.map(qualification => (
                  <MenuItem key={qualification} value={qualification}>
                    {qualification === 'qualifie' ? 'Qualifiés' :
                     qualification === 'disqualifie' ? 'Disqualifiés' :
                     'En attente'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tableau */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                      indeterminate={selectedContacts.length > 0 && selectedContacts.length < filteredContacts.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                    <TableCell>Étape</TableCell>
                  <TableCell>Prénom</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Téléphone</TableCell>
                  <TableCell>Entreprise</TableCell>
                  <TableCell>Job Title</TableCell>
                    <TableCell>LinkedIn</TableCell>
                  <TableCell>Secteur</TableCell>
                  <TableCell>Effectif</TableCell>
                    <TableCell>Zone géographique</TableCell>
                  <TableCell>Qualification</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {filteredContacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                          {leadsLoading 
                            ? 'Chargement des données...' 
                            : useSupabaseData && leads.length === 0
                              ? 'Aucun lead trouvé pour cette campagne' 
                              : 'Aucun contact trouvé pour les filtres sélectionnés'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredContacts.map((contact) => (
                      <TableRow key={contact.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => handleSelectContact(contact.id)}
                      />
                    </TableCell>
                        <TableCell>{contact.statut}</TableCell>
                    <TableCell>{contact.prenom}</TableCell>
                    <TableCell>{contact.nom}</TableCell>
                    <TableCell>{contact.email}</TableCell>
                    <TableCell>{contact.phone}</TableCell>
                        <TableCell>{contact.entreprise}</TableCell>
                        <TableCell>{contact.jobTitle}</TableCell>
                    <TableCell>
                      <IconButton
                        href={contact.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        color="primary"
                      >
                        <LinkedIn />
                      </IconButton>
                    </TableCell>
                    <TableCell>{contact.secteur}</TableCell>
                    <TableCell>{contact.effectif}</TableCell>
                    <TableCell>{contact.zoneGeographique}</TableCell>
                    <TableCell>
                      <Chip
                        label={contact.estQualifie === null ? 'En attente' : contact.estQualifie ? 'Qualifié' : 'Disqualifié'}
                        color={contact.estQualifie === null ? 'default' : contact.estQualifie ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                    ))
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        </TabPanel>

        {/* Tab for Questionnaire */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Questionnaire de campagne
          </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Complétez ce questionnaire pour nous aider à comprendre vos besoins et objectifs. Ces réponses nous aideront à personnaliser votre campagne.
            </Typography>
            
            {id && (
              <CampaignQuestionnaire 
                campaignId={id} 
                initialData={safeJsonToQuestionnaireData(campaignData?.questionnaire_data)}
                onSave={handleQuestionnaireSaved}
              />
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}; 